import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Ticket, DollarSign, TrendingUp, BookOpen, AlertTriangle, Plus, Trash2, Edit3, X, Star, Eye, EyeOff } from 'lucide-react';
import { apiFetch, toast } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function for status styling
function getTicketStatusClassName(status) {
  if (status === 'open') return 'bg-[#00f0ff]/20 text-[#00f0ff]';
  if (status === 'in_progress') return 'bg-[#ffa500]/20 text-[#ffa500]';
  if (status === 'resolved') return 'bg-[#00ff00]/20 text-[#00ff00]';
  return 'bg-gray-500/20 text-gray-400';
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [updateStatus, setUpdateStatus] = useState('');
  const [updatePriority, setUpdatePriority] = useState('');
  const [activeTab, setActiveTab] = useState('tickets'); // 'tickets' | 'kb' | 'reviews'
  const [showEscalatedOnly, setShowEscalatedOnly] = useState(false);
  // Knowledge base state
  const [kbEntries, setKbEntries] = useState([]);
  const [kbLoading, setKbLoading] = useState(false);
  const [showKbForm, setShowKbForm] = useState(false);
  const [editingKb, setEditingKb] = useState(null);
  const [kbForm, setKbForm] = useState({ title: '', content: '', category: 'general', tags: '' });
  // Reviews moderation state
  const [reviewsData, setReviewsData] = useState({ reviews: [], pending_count: 0 });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsFilter, setReviewsFilter] = useState('pending'); // 'pending' | 'approved' | 'all'

  useEffect(() => {
    loadData();
    loadReviews(); // load to show pending badge
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, ticketsRes] = await Promise.all([
        apiFetch(`${API}/admin/stats`),
        apiFetch(`${API}/tickets?skip=0&limit=50`)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (ticketsRes.ok) {
        const data = await ticketsRes.json();
        setTickets(data.tickets || data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTicket = async (ticketId) => {
    try {
      const updateData = {};
      if (updateStatus) updateData.status = updateStatus;
      if (updatePriority) updateData.priority = updatePriority;

      const response = await apiFetch(`${API}/tickets/${ticketId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        setSelectedTicket(null);
        setUpdateStatus('');
        setUpdatePriority('');
        loadData();
        toast.success('Ticket updated');
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  };

  const loadKb = async () => {
    setKbLoading(true);
    try {
      const res = await apiFetch(`${API}/admin/kb`);
      if (res.ok) setKbEntries(await res.json());
    } catch (e) {
      console.error('KB load error', e);
    } finally {
      setKbLoading(false);
    }
  };

  const submitKb = async (e) => {
    e.preventDefault();
    const tagsArr = kbForm.tags ? kbForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
    const payload = {
      title: kbForm.title,
      content: kbForm.content,
      category: kbForm.category || 'general',
      tags: tagsArr,
    };
    try {
      const url = editingKb ? `${API}/admin/kb/${editingKb}` : `${API}/admin/kb`;
      const method = editingKb ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setKbForm({ title: '', content: '', category: 'general', tags: '' });
        setShowKbForm(false);
        setEditingKb(null);
        await loadKb();
        toast.success(editingKb ? 'Entry updated' : 'Entry saved');
      }
    } catch (e) {
      console.error('KB save error', e);
    }
  };

  const editKb = (entry) => {
    setEditingKb(entry.kb_id);
    setKbForm({
      title: entry.title,
      content: entry.content,
      category: entry.category || 'general',
      tags: (entry.tags || []).join(', '),
    });
    setShowKbForm(true);
  };

  const deleteKb = async (kb_id) => {
    if (!window.confirm('Delete this KB entry? The AI will no longer use it.')) return;
    try {
      const res = await apiFetch(`${API}/admin/kb/${kb_id}`, { method: 'DELETE' });
      if (res.ok) {
        await loadKb();
        toast.success('Entry deleted');
      }
    } catch (e) {
      console.error('KB delete error', e);
    }
  };

  useEffect(() => {
    if (activeTab === 'kb' && kbEntries.length === 0) loadKb();
    if (activeTab === 'reviews') loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reviewsFilter]);

  const loadReviews = async () => {
    setReviewsLoading(true);
    try {
      const res = await apiFetch(`${API}/admin/reviews?status=${reviewsFilter}`);
      if (res.ok) setReviewsData(await res.json());
    } catch (e) {
      console.error('Reviews load error', e);
    } finally {
      setReviewsLoading(false);
    }
  };

  const moderateReview = async (reviewId, action) => {
    try {
      const res = await apiFetch(`${API}/admin/reviews/${reviewId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        toast.success(action === 'approve' ? 'Review approved and published' : 'Review hidden');
        loadReviews();
      }
    } catch (e) {
      console.error('Review moderation error', e);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      const res = await apiFetch(`${API}/admin/reviews/${reviewId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Review deleted');
        loadReviews();
      }
    } catch (e) {
      console.error('Review delete error', e);
    }
  };

  const hideAllPublicReviews = async () => {
    if (!window.confirm('Move ALL currently-live testimonials back to Pending so you can re-audit them? They will no longer show on the landing page until you re-approve each one.')) return;
    try {
      const res = await apiFetch(`${API}/admin/reviews/hide-all-public`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.hidden} testimonial${data.hidden === 1 ? '' : 's'} moved back to Pending`);
        loadReviews();
      }
    } catch (e) {
      console.error('Bulk hide error', e);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00f0ff] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#00f0ff] glow">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="grid-background absolute inset-0 opacity-20"></div>
      
      {/* Header */}
      <nav className="relative z-10 border-b border-[#00f0ff]/30 bg-[#0a0a1f]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#ff00ff] to-[#b026ff] text-transparent bg-clip-text">
              Admin Dashboard
            </h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-[#00f0ff] hover:text-[#ff00ff] transition-colors"
              data-testid="back-to-user-dashboard-button"
            >
              <ArrowLeft size={20} />
              <span>Back to User Dashboard</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid md:grid-cols-5 gap-6 mb-8">
          <div className="glass rounded-xl p-6 glow-box hover-lift" data-testid="admin-stat-users">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#00f0ff]/70 text-sm">Total Users</p>
                <p className="text-4xl font-bold text-[#00f0ff] glow mt-2">{stats?.total_users || 0}</p>
              </div>
              <Users className="text-[#00f0ff]" size={48} />
            </div>
          </div>

          <div className="glass rounded-xl p-6 glow-box-magenta hover-lift" data-testid="admin-stat-tickets">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#ff00ff]/70 text-sm">Total Tickets</p>
                <p className="text-4xl font-bold text-[#ff00ff] glow mt-2">{stats?.total_tickets || 0}</p>
              </div>
              <Ticket className="text-[#ff00ff]" size={48} />
            </div>
          </div>

          <div className="glass rounded-xl p-6 glow-box-purple hover-lift" data-testid="admin-stat-open">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#b026ff]/70 text-sm">Open Tickets</p>
                <p className="text-4xl font-bold text-[#b026ff] glow mt-2">{stats?.open_tickets || 0}</p>
              </div>
              <TrendingUp className="text-[#b026ff]" size={48} />
            </div>
          </div>

          <button
            onClick={() => { setActiveTab('tickets'); setShowEscalatedOnly(true); }}
            className="text-left glass rounded-xl p-6 hover-lift border-2 border-[#ff00ff]/40 hover:border-[#ff00ff] transition-all"
            data-testid="admin-stat-escalated"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#ff00ff]/70 text-sm">Escalated</p>
                <p className="text-4xl font-bold text-[#ff00ff] glow mt-2">{stats?.escalated_tickets || 0}</p>
              </div>
              <AlertTriangle className="text-[#ff00ff]" size={48} />
            </div>
          </button>

          <div className="glass rounded-xl p-6 glow-box hover-lift" data-testid="admin-stat-revenue">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#00f0ff]/70 text-sm">Total Revenue</p>
                <p className="text-4xl font-bold text-[#00f0ff] glow mt-2">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
              </div>
              <DollarSign className="text-[#00f0ff]" size={48} />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setActiveTab('tickets')}
            className={`px-5 py-2 rounded-lg font-bold flex items-center space-x-2 transition-all ${activeTab === 'tickets' ? 'bg-[#ff00ff] text-white' : 'bg-[#0a0a1f]/50 border border-[#00f0ff]/30 text-[#00f0ff] hover:border-[#00f0ff]'}`}
            data-testid="tab-tickets"
          >
            <Ticket size={18} />
            <span>Tickets</span>
          </button>
          <button
            onClick={() => setActiveTab('kb')}
            className={`px-5 py-2 rounded-lg font-bold flex items-center space-x-2 transition-all ${activeTab === 'kb' ? 'bg-[#ff00ff] text-white' : 'bg-[#0a0a1f]/50 border border-[#00f0ff]/30 text-[#00f0ff] hover:border-[#00f0ff]'}`}
            data-testid="tab-kb"
          >
            <BookOpen size={18} />
            <span>Knowledge Base</span>
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`relative px-5 py-2 rounded-lg font-bold flex items-center space-x-2 transition-all ${activeTab === 'reviews' ? 'bg-[#ff00ff] text-white' : 'bg-[#0a0a1f]/50 border border-[#00f0ff]/30 text-[#00f0ff] hover:border-[#00f0ff]'}`}
            data-testid="tab-reviews"
          >
            <Star size={18} />
            <span>Reviews</span>
            {reviewsData.pending_count > 0 && (
              <span
                className="ml-1 px-2 py-0.5 text-xs rounded-full bg-[#ffa500] text-[#0a0a1f] font-bold"
                data-testid="reviews-pending-badge"
              >
                {reviewsData.pending_count}
              </span>
            )}
          </button>
        </div>

        {/* Tickets Management */}
        {activeTab === 'tickets' && (
        <div className="glass rounded-xl p-6 glow-box">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-[#00f0ff]">All Tickets</h3>
            <label className="flex items-center space-x-2 text-sm text-[#ff00ff] cursor-pointer">
              <input
                type="checkbox"
                checked={showEscalatedOnly}
                onChange={(e) => setShowEscalatedOnly(e.target.checked)}
                className="accent-[#ff00ff]"
                data-testid="escalated-filter-checkbox"
              />
              <span>Show escalated only</span>
            </label>
          </div>
          <div className="space-y-4">
            {tickets
              .filter(t => !showEscalatedOnly || t.escalated_to_human)
              .map(ticket => (
              <div
                key={ticket.ticket_id}
                className={`p-4 rounded-lg border bg-[#0a0a1f]/50 ${ticket.escalated_to_human ? 'border-[#ff00ff] border-2' : 'border-[#00f0ff]/30'}`}
                data-testid={`admin-ticket-${ticket.ticket_id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 flex-wrap">
                      <h4 className="text-lg font-bold text-[#00f0ff]">{ticket.title}</h4>
                      {ticket.escalated_to_human && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#ff00ff]/20 text-[#ff00ff] border border-[#ff00ff]" data-testid={`escalated-badge-${ticket.ticket_id}`}>
                          🛟 ESCALATED
                        </span>
                      )}
                    </div>
                    <p className="text-[#00f0ff]/70 text-sm mt-1">{ticket.service_name}</p>
                    <p className="text-[#00f0ff]/60 text-sm mt-2">User ID: {ticket.user_id}</p>
                    {ticket.escalation_reason && (
                      <p className="text-[#ff00ff]/80 text-xs mt-1 italic">Reason: {ticket.escalation_reason}</p>
                    )}
                  </div>
                  <div className="flex flex-col space-y-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTicketStatusClassName(ticket.status)}`}>
                      {ticket.status.replace('_', ' ').toUpperCase()}
                    </span>
                    <button
                      onClick={() => setSelectedTicket(selectedTicket === ticket.ticket_id ? null : ticket.ticket_id)}
                      className="px-3 py-1 bg-[#ff00ff] text-white text-xs rounded-full hover:bg-[#b026ff] transition-colors"
                      data-testid={`update-ticket-${ticket.ticket_id}-button`}
                    >
                      Update
                    </button>
                    <button
                      onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                      className="px-3 py-1 border border-[#00f0ff] text-[#00f0ff] text-xs rounded-full hover:bg-[#00f0ff] hover:text-[#0a0a1f] transition-all"
                      data-testid={`view-ticket-${ticket.ticket_id}-button`}
                    >
                      View Chat
                    </button>
                  </div>
                </div>

                {selectedTicket === ticket.ticket_id && (
                  <div className="mt-4 p-4 border-t border-[#00f0ff]/30">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[#00f0ff] text-sm mb-2">Status</label>
                        <select
                          value={updateStatus || ticket.status}
                          onChange={(e) => setUpdateStatus(e.target.value)}
                          className="w-full px-3 py-2 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff]"
                          data-testid="status-select"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[#00f0ff] text-sm mb-2">Priority</label>
                        <select
                          value={updatePriority || ticket.priority}
                          onChange={(e) => setUpdatePriority(e.target.value)}
                          className="w-full px-3 py-2 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff]"
                          data-testid="priority-select"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </div>
                    <button
                      onClick={() => updateTicket(ticket.ticket_id)}
                      className="mt-4 px-6 py-2 btn-primary text-white font-bold rounded-lg"
                      data-testid="save-updates-button"
                    >
                      Save Updates
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Knowledge Base Management */}
        {activeTab === 'kb' && (
        <div className="glass rounded-xl p-6 glow-box">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-[#00f0ff]">Knowledge Base</h3>
              <p className="text-[#00f0ff]/60 text-sm mt-1">Entries here are fed into the AI agent so it can answer with your guidance.</p>
            </div>
            <button
              onClick={() => { setShowKbForm(true); setEditingKb(null); setKbForm({ title: '', content: '', category: 'general', tags: '' }); }}
              className="px-4 py-2 btn-primary text-white font-bold rounded-lg flex items-center space-x-2"
              data-testid="new-kb-button"
            >
              <Plus size={18} />
              <span>New Entry</span>
            </button>
          </div>

          {showKbForm && (
            <form onSubmit={submitKb} className="mb-6 p-5 rounded-lg border border-[#ff00ff]/40 bg-[#0a0a1f]/40 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-[#ff00ff] font-bold">{editingKb ? 'Edit Entry' : 'New Knowledge Base Entry'}</h4>
                <button type="button" onClick={() => { setShowKbForm(false); setEditingKb(null); }} className="text-[#00f0ff]/70 hover:text-[#ff00ff]">
                  <X size={20} />
                </button>
              </div>
              <input
                value={kbForm.title}
                onChange={(e) => setKbForm({ ...kbForm, title: e.target.value })}
                placeholder="Title (e.g., 'Slow Mac startup — first checks')"
                className="w-full px-3 py-2 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff]"
                required
                data-testid="kb-title-input"
              />
              <div className="grid md:grid-cols-2 gap-3">
                <input
                  value={kbForm.category}
                  onChange={(e) => setKbForm({ ...kbForm, category: e.target.value })}
                  placeholder="Category (e.g., performance, networking)"
                  className="w-full px-3 py-2 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff]"
                  data-testid="kb-category-input"
                />
                <input
                  value={kbForm.tags}
                  onChange={(e) => setKbForm({ ...kbForm, tags: e.target.value })}
                  placeholder="Tags, comma separated"
                  className="w-full px-3 py-2 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff]"
                  data-testid="kb-tags-input"
                />
              </div>
              <textarea
                value={kbForm.content}
                onChange={(e) => setKbForm({ ...kbForm, content: e.target.value })}
                placeholder="Content — step-by-step troubleshooting, FAQs, or company-specific guidance the AI should follow."
                className="w-full px-3 py-2 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] min-h-[140px]"
                required
                data-testid="kb-content-input"
              />
              <button type="submit" className="px-5 py-2 btn-primary text-white font-bold rounded-lg" data-testid="kb-save-button">
                {editingKb ? 'Update Entry' : 'Save Entry'}
              </button>
            </form>
          )}

          {kbLoading ? (
            <p className="text-[#00f0ff]/70">Loading…</p>
          ) : kbEntries.length === 0 ? (
            <p className="text-[#00f0ff]/70 text-center py-8">No entries yet. Add your first one above.</p>
          ) : (
            <div className="space-y-3">
              {kbEntries.map(entry => (
                <div key={entry.kb_id} className="p-4 rounded-lg border border-[#00f0ff]/30 bg-[#0a0a1f]/40" data-testid={`kb-entry-${entry.kb_id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 flex-wrap">
                        <h4 className="text-[#00f0ff] font-bold">{entry.title}</h4>
                        <span className="px-2 py-0.5 text-[10px] rounded-full bg-[#b026ff]/20 text-[#b026ff] border border-[#b026ff]/40">
                          {entry.category}
                        </span>
                      </div>
                      <p className="text-[#00f0ff]/70 text-sm mt-2 whitespace-pre-wrap">{entry.content}</p>
                      {entry.tags && entry.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {entry.tags.map((t) => (
                            <span key={`${entry.kb_id}-${t}`} className="px-2 py-0.5 text-[10px] rounded bg-[#00f0ff]/10 text-[#00f0ff]/70 border border-[#00f0ff]/20">#{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button onClick={() => editKb(entry)} className="p-2 text-[#00f0ff] hover:bg-[#00f0ff]/10 rounded" data-testid={`kb-edit-${entry.kb_id}`}>
                        <Edit3 size={16} />
                      </button>
                      <button onClick={() => deleteKb(entry.kb_id)} className="p-2 text-[#ff00ff] hover:bg-[#ff00ff]/10 rounded" data-testid={`kb-delete-${entry.kb_id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Reviews Moderation */}
        {activeTab === 'reviews' && (
        <div className="glass rounded-xl p-6 glow-box">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h3 className="text-2xl font-bold text-[#00f0ff]">Customer Reviews</h3>
            <div className="flex flex-wrap gap-2 items-center" data-testid="reviews-filter-group">
              {['pending', 'approved', 'all'].map(f => (
                <button
                  key={f}
                  onClick={() => setReviewsFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${reviewsFilter === f ? 'bg-[#ff00ff] text-white' : 'border border-[#00f0ff]/40 text-[#00f0ff] hover:border-[#00f0ff]'}`}
                  data-testid={`reviews-filter-${f}`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              <button
                onClick={hideAllPublicReviews}
                className="ml-2 px-3 py-1.5 rounded-lg text-sm font-semibold border border-[#ffa500]/60 text-[#ffa500] hover:bg-[#ffa500]/10 transition-all"
                data-testid="hide-all-public-button"
                title="Move all live testimonials back to Pending for re-audit"
              >
                Re-audit all live
              </button>
            </div>
          </div>

          <p className="text-[#00f0ff]/60 text-sm mb-4">
            5★ reviews marked &quot;share as testimonial&quot; go live automatically. 4★ reviews need your approval before they appear on the landing page.
          </p>

          {(() => {
            if (reviewsLoading) {
              return <p className="text-[#00f0ff]/70">Loading…</p>;
            }
            if (reviewsData.reviews.length === 0) {
              return (
                <p className="text-[#00f0ff]/70 text-center py-8" data-testid="reviews-empty-state">
                  No reviews in this view yet.
                </p>
              );
            }
            const borderClassFor = (r) => {
              if (r.is_public) return 'border-[#00ff00]/40';
              if (r.allow_public) return 'border-[#ffa500]/40';
              return 'border-[#00f0ff]/20';
            };
            return (
              <div className="space-y-4">
                {reviewsData.reviews.map(r => (
                <div
                  key={r.review_id}
                  className={`p-4 rounded-lg border bg-[#0a0a1f]/40 ${borderClassFor(r)}`}
                  data-testid={`review-row-${r.review_id}`}
                >
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2 flex-wrap">
                        <div className="flex items-center">
                          {Array.from({ length: r.rating }).map((_, i) => (
                            <Star key={i} size={14} className="fill-[#ffa500] text-[#ffa500]" />
                          ))}
                        </div>
                        <span className="text-[#ff00ff] font-semibold text-sm">
                          {r.user_name || r.display_name || 'Anonymous'}
                        </span>
                        <span className="text-[#00f0ff]/50 text-xs">
                          {r.user_email}
                        </span>
                        {r.is_public && (
                          <span className="px-2 py-0.5 text-[10px] rounded bg-[#00ff00]/20 text-[#00ff00] border border-[#00ff00]/40">
                            LIVE
                          </span>
                        )}
                        {r.allow_public && !r.is_public && (
                          <span className="px-2 py-0.5 text-[10px] rounded bg-[#ffa500]/20 text-[#ffa500] border border-[#ffa500]/40">
                            PENDING
                          </span>
                        )}
                        {!r.allow_public && (
                          <span className="px-2 py-0.5 text-[10px] rounded bg-gray-500/20 text-gray-400 border border-gray-500/40">
                            INTERNAL
                          </span>
                        )}
                      </div>
                      {r.ticket_title && (
                        <p className="text-[#00f0ff]/50 text-xs mb-1">Ticket: {r.ticket_title}</p>
                      )}
                      <p className="text-[#00f0ff]/90 text-sm whitespace-pre-wrap italic">&ldquo;{r.review_text}&rdquo;</p>
                    </div>
                    <div className="flex space-x-2">
                      {r.allow_public && !r.is_public && (
                        <button
                          onClick={() => moderateReview(r.review_id, 'approve')}
                          className="px-3 py-1.5 text-xs rounded-lg bg-[#00ff00]/20 text-[#00ff00] border border-[#00ff00]/40 hover:bg-[#00ff00]/30 flex items-center space-x-1"
                          data-testid={`review-approve-${r.review_id}`}
                        >
                          <Eye size={14} />
                          <span>Approve</span>
                        </button>
                      )}
                      {r.is_public && (
                        <button
                          onClick={() => moderateReview(r.review_id, 'hide')}
                          className="px-3 py-1.5 text-xs rounded-lg bg-[#ffa500]/20 text-[#ffa500] border border-[#ffa500]/40 hover:bg-[#ffa500]/30 flex items-center space-x-1"
                          data-testid={`review-hide-${r.review_id}`}
                        >
                          <EyeOff size={14} />
                          <span>Hide</span>
                        </button>
                      )}
                      <button
                        onClick={() => deleteReview(r.review_id)}
                        className="px-3 py-1.5 text-xs rounded-lg bg-[#ff00ff]/20 text-[#ff00ff] border border-[#ff00ff]/40 hover:bg-[#ff00ff]/30 flex items-center space-x-1"
                        data-testid={`review-delete-${r.review_id}`}
                      >
                        <Trash2 size={14} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            );
          })()}
        </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;